import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { Button, Field, Loader, Select } from '../../../ui';
import { actorPayload, TaskTypeCfg, tasksApi } from '../../../Tasks/tasksApi';
import { Callout, Drawer } from './ui-pending';
import './CreateTaskModal.scss';

/**
 * Raise a ticket for a learner by hand, from their profile.
 *
 * It always goes to the RM who already owns the learner — never to
 * "whoever is free". That is the whole point: an agent logging something a
 * learner said needs it to reach the person who actually looks after them.
 * `requireExistingRm` makes the service enforce that rather than trusting
 * this screen, so an unmapped learner is refused instead of being handed an
 * RM as a side effect of someone writing a note.
 *
 * Freshchat is the next caller for this. Today an agent ends a chat and
 * raises a Freshdesk ticket that lives outside the RM system entirely; the
 * same create, from a plugin, puts it in the learner's RM's queue instead.
 */

interface Props {
  studentExternalId: string;
  studentName: string;
  /** Shown so the person can see where it will land before they send it. */
  rmName: string | null;
  phoneE164?: string;
  email?: string;
  onClose: () => void;
  onCreated?: (taskNumber: number) => void;
}

const RMT = (n: number): string => `RMT-${String(n).padStart(4, '0')}`;

const CreateTaskModal: React.FC<Props> = ({
  studentExternalId, studentName, rmName, phoneE164, email, onClose, onCreated,
}) => {
  const [types, setTypes] = useState<TaskTypeCfg[] | null>(null);
  const [typeKey, setTypeKey] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  /**
   * Fixed when the dialog opens, so the create is idempotent per dialog:
   * a double-click or a retry returns the same ticket, while deliberately
   * raising a second one later (a fresh dialog) still works.
   */
  const recordId = useMemo(
    () =>
      `manual:${studentExternalId}:${
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`
      }`,
    [studentExternalId],
  );

  useEffect(() => {
    let live = true;
    tasksApi<TaskTypeCfg[]>('/v1/task-types')
      .then((rows) => {
        if (!live) return;
        // GET /v1/task-types already returns only active types.
        const usable = [...rows].sort((a, b) => a.name.localeCompare(b.name));
        setTypes(usable);
        if (usable.length) setTypeKey(usable[0].key);
      })
      .catch(() => live && setTypes([]));
    return () => { live = false; };
  }, []);

  const chosen = types?.find((t) => t.key === typeKey);

  const submit = async (): Promise<void> => {
    if (!typeKey) return;
    setBusy(true);
    try {
      const res = await tasksApi<{ task: { taskNumber: number; assignee?: { name: string } }; created: boolean }>(
        '/v1/tasks',
        {
          method: 'POST',
          body: JSON.stringify({
            taskTypeKey: typeKey,
            customerExternalId: studentExternalId,
            customerName: studentName,
            ...(phoneE164 ? { customerPhoneE164: phoneE164 } : {}),
            ...(email ? { customerEmail: email } : {}),
            source: 'MANUAL',
            sourceEvent: 'cms.manual',
            sourceRecordId: recordId,
            // The service refuses rather than inventing an RM.
            requireExistingRm: true,
            ...(note.trim() ? { note: note.trim() } : {}),
            // actorPayload() sends strictly what ActorDto whitelists. The
            // service rejects unknown properties, and the fuller helper
            // carries an email meant only for agent self-provisioning.
            actor: actorPayload(),
          }),
        },
      );
      const n = res?.task?.taskNumber;
      toast.success(
        res?.created === false
          ? `Already raised — ${RMT(n)}`
          : `${RMT(n)} raised for ${res?.task?.assignee?.name ?? 'their RM'}`,
      );
      onCreated?.(n);
      onClose();
    } catch (err: any) {
      // The service's NO_RM_MAPPED message is written for this reader.
      toast.error(err?.message || 'Could not raise the ticket');
    } finally {
      setBusy(false);
    }
  };

  // Drawer portals to <body>: the theme puts a transform on a page wrapper,
  // and a position:fixed child resolves against that instead of the viewport.
  return (
    <Drawer
      open
      onClose={onClose}
      placement="center"
      size="md"
      title="Raise a ticket"
      subtitle={
        <>
          for {studentName} · goes to{' '}
          {rmName ? <span className="ds-strong">{rmName}</span> : 'their RM'}
        </>
      }
      footer={
        <>
          <Button size="sm" variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button
            size="sm"
            variant="primary"
            disabled={!typeKey || !rmName}
            loading={busy}
            onClick={() => void submit()}
          >
            {busy ? 'Raising…' : 'Raise ticket'}
          </Button>
        </>
      }
    >
      {!rmName && (
        <Callout tone="danger">
          This learner has no RM yet. Assign one on this page first — a ticket
          raised now would pick an RM for them as a side effect.
        </Callout>
      )}

      {types === null ? (
        <Loader label="Loading ticket types…" />
      ) : types.length === 0 ? (
        <Callout tone="neutral" title="Ticket type">Could not load the ticket types.</Callout>
      ) : (
        <Field label="Ticket type" hint={chosen?.description || undefined}>
          {(control) => (
            <Select
              {...control}
              size="sm"
              value={typeKey}
              onChange={(e) => setTypeKey(e.target.value)}
              options={types.map((t) => ({ value: t.key, label: t.name }))}
            />
          )}
        </Field>
      )}

      <Field label="What happened?" hint="Optional, shown on the timeline">
        {(control) => (
          <textarea
            {...control}
            className="ds-input ctm-note"
            rows={3}
            value={note}
            placeholder="What the learner said, or why this needs an RM"
            onChange={(e) => setNote(e.target.value)}
          />
        )}
      </Field>
    </Drawer>
  );
};

export default CreateTaskModal;
